/**
 * The "What Our Customers Say" testimonial carousel: one slide per customer quote, each with the
 * quote, the person's name and label, back/next buttons, and the same embedded YouTube video
 * (`@ryerson/frontend.media` → `Video`, which shows `thumbnailUrl` until the visitor presses play).
 *
 * This is the one implementation; the other two components in this folder wrap it:
 * - `./WhatOurCustomersCustom.tsx` adds the contact band after it (34 importers: the
 *   `pages/metal-solutions/**` pages and
 *   `components/BarTubeStructuralProcessing/BasePage/BasePage.tsx`);
 * - `./WhatOurCustomers.tsx` is a prop-less wrapper used only by
 *   `components/Contentful/SubProduct.tsx`.
 * Direct importers are `components/Contentful/Product.tsx` and
 * `components/HomePage/STSHomepage.tsx`.
 *
 * Copy comes from `./RefactorContent.tsx` (Ryerson) or `./STSContent.tsx` (Southern Tool Steel,
 * dormant build), chosen by `NEXT_PUBLIC_COMPANY`, unless a caller passes `content`. No caller in
 * this repo passes it today. `./Content.tsx` and `./Slides.tsx` are unused.
 *
 * Below the `lg` breakpoint the carousel has no header, because the page's `AllSections` accordion
 * tab already shows the section title (`components/Shared/AllSections/AllSections.tsx`).
 */
import React from "react";
import { Media } from "@ryerson/frontend.next";
import { Typography } from "@ryerson/frontend.typography";
import { Icon } from "@ryerson/frontend.assets";
import { useTheme } from "@ryerson/frontend.theme";
import { Video } from "@ryerson/frontend.media";
import { Carousel, ISlideBaseProps } from "@ryerson/frontend.carousel";
import { WhatOurContentSays } from "./RefactorContent";
import { STSWhatOurContentSays } from "./STSContent";
import { ContentSection } from "@ryerson/frontend.layout";
import { useApplication } from "@ryerson/frontend.application";
import styled from "@emotion/styled";
import { css } from "@emotion/react";

/** One testimonial. */
export type Slide = {
	quote: string;
	/** The customer's name, shown in bold under the quote. */
	person: string;
	/** Shown under the name (a company). Empty in every language of the live Ryerson copy. */
	personLabel: string;
};

/**
 * Copy for one language of the carousel. `./RefactorContent.tsx`, `./STSContent.tsx` and the unused
 * `./Content.tsx` are written to this shape.
 */
export type WhatOurCustomersSayContent = {
	/**
	 * Carousel heading on desktop. Pages also use it as the `AllSections` title and anchor slug.
	 */
	title: string;
	/** Shorter label for page sub-navigation links ("Testimonials"). */
	navLabel: string;
	/** YouTube video id, embedded beside every slide. */
	videoId: string;
	/**
	 * Poster image shown before the video plays. When absent or empty, the YouTube preview is used.
	 */
	thumbnailUrl?: string;
	slides: Slide[];
};

/**
 * Props for one carousel slide. `back` and `next` come from `ISlideBaseProps`; `Carousel`
 * (`@ryerson/frontend.carousel`) supplies them to each child.
 */
export interface ICustomSlideProps extends ISlideBaseProps {
	quote: string;
	person: string;
	personLabel: string;
	videoId: string;
	thumbnailUrl?: string;
}

/** Props of the default export. */
export type WhatOurCustomersSayProps = {
	/** Copy override. When absent, the brand's copy in the visitor's language is used. */
	content?: WhatOurCustomersSayContent;
};

const SlideContainer = styled.div`
	display: block;
	width: 100%;
	height: 560px;
	position: relative;
`;

const SlideQuote = styled.div`
	display: block;
	width: calc(100% - 130px);
	max-width: 760px;
	height: auto;
	position: absolute;
	top: 50px;
	left: 0px;
`;

const SlideVideo = styled.div`
	display: block;
	width: 560px;
	height: 360px;
	position: absolute;
	bottom: 0px;
	right: 0px;
`;

const SlideButtons = styled.div`
	display: block;
	position: absolute;
	top: 50px;
	width: 125px;
	height: 60px;
	right: 0px;
`;

const CircleIcon = styled.div`
	display: inline-block;
	width: 60px;
	height: 60px;
	border-radius: 60px;
	text-align: center;
	line-height: 67px;
	cursor: pointer;
`;

/**
 * One slide: back/next buttons, the video, and the quote with its attribution. On desktop the quote
 * sits top left and the video bottom right; below `lg` the buttons, video and quote stack, with the
 * quote pushed down by top padding to clear the absolutely positioned video.
 */
const VideoSlide: React.FC<ICustomSlideProps> = ({
	back = () => {},
	next = () => {},
	quote,
	person,
	personLabel,
	videoId,
	thumbnailUrl = null,
}) => {
	const { theme } = useTheme();

	return (
		<SlideContainer
			css={css`
				@media (max-width: ${theme.breakpoints.lg}) {
					min-height: 600px;
					height: auto;
					padding-bottom: 40px;
				}
			`}
		>
			<SlideButtons
				css={css`
					@media (max-width: ${theme.breakpoints.lg}) {
						top: 220px;
						width: 85px;
						height: 40px;
						right: unset;
						left: 195px;
						z-index: 2;
					}
				`}
			>
				<CircleIcon
					onClick={() => {
						back();
					}}
					css={css`
						background-color: ${theme.colors.primary.darkGray};
						margin-right: 5px;
						@media (max-width: ${theme.breakpoints.lg}) {
							width: 40px;
							height: 40px;
							border-radius: 40px;
							line-height: 42px;
						}
					`}
				>
					<Media greaterThanOrEqual="lg">
						<Icon icon="chevron-left" color={theme.colors.primary.white} size="sm" />
					</Media>
					<Media lessThan="lg">
						<Icon icon="chevron-left" color={theme.colors.primary.white} size="xs" />
					</Media>
				</CircleIcon>
				<CircleIcon
					onClick={() => {
						next();
					}}
					css={css`
						background-color: ${theme.colors.primary.darkGray};
						@media (max-width: ${theme.breakpoints.lg}) {
							width: 40px;
							height: 40px;
							border-radius: 40px;
							line-height: 42px;
						}
					`}
				>
					<Media greaterThanOrEqual="lg">
						<Icon icon="chevron-right" color={theme.colors.primary.white} size="sm" />
					</Media>
					<Media lessThan="lg">
						<Icon icon="chevron-right" color={theme.colors.primary.white} size="xs" />
					</Media>
				</CircleIcon>
			</SlideButtons>

			<Media greaterThanOrEqual="lg">
				<SlideVideo
					css={css`
						@media (max-width: ${theme.breakpoints.xl}) {
							right: 60px;
						}
					`}
				>
					<Video
						buttonOrientation="right"
						imageUrl={
							thumbnailUrl && thumbnailUrl.length > 0 ? thumbnailUrl : undefined
						}
						videoId={videoId}
					/>
				</SlideVideo>
			</Media>
			<Media lessThan="lg">
				<SlideVideo
					css={css`
						@media (max-width: ${theme.breakpoints.lg}) {
							width: 100%;
							max-width: 450px;
							height: 200px;
							bottom: unset;
							right: unset;
							left: px;
							top: 40px;
							z-index: 1;
						}
					`}
				>
					<Video
						buttonOrientation="top-right"
						imageUrl={
							thumbnailUrl && thumbnailUrl.length > 0 ? thumbnailUrl : undefined
						}
						videoId={videoId}
					/>
				</SlideVideo>
			</Media>
			<SlideQuote
				css={css`
					@media (max-width: ${theme.breakpoints.lg}) {
						width: 100%;
						max-width: 450px;
						top: unset;
						bottom: unset;
						left: unset;
						right: unset;
						display: block;
						padding-top: 280px;
						position: relative;
					}
				`}
			>
				<Typography
					variant="h2"
					css={css`
						letter-spacing: -2px;
						position: relative;
						@media (max-width: ${theme.breakpoints.lg}) {
							font-size: 20px !important;
						}
					`}
					color={theme.colors.primary.gray}
				>
					<Typography
						variant="div"
						color={theme.colors.primary.lightGray}
						css={css`
							position: absolute;
							left: -20px;
							@media (max-width: ${theme.breakpoints.lg}) {
								left: -10px;
							}
						`}
					>
						“
					</Typography>
					{quote}
					<Typography
						variant="span"
						css={css`
							display: inline-block;
							margin-left: 5px;
						`}
						color={theme.colors.primary.lightGray}
					>
						”
					</Typography>
				</Typography>
				<Typography
					variant="div"
					css={css`
						margin-top: 48px;
						@media (max-width: ${theme.breakpoints.lg}) {
							margin-top: 28px;
						}
					`}
					size="md"
					type="primary"
					weight="bold"
				>
					{person}
				</Typography>
				<Typography variant="div" size="md" type="primary">
					{personLabel}
				</Typography>
			</SlideQuote>
		</SlideContainer>
	);
};

/**
 * The testimonial carousel, in a mobile and a desktop layout switched by `Media` at `lg`.
 * `Carousel` renders one slide at a time; on desktop its header shows `title` and an "n of m"
 * counter.
 */
const WhatOurCustomers: React.FC<WhatOurCustomersSayProps> = ({ content }) => {
	const {
		localization: { language },
	} = useApplication();
	// Default copy by brand; the STS build is dormant.
	if (!content) {
		content =
			process.env.NEXT_PUBLIC_COMPANY === "southernToolSteel"
				? STSWhatOurContentSays[language]
				: WhatOurContentSays[language];
	}
	return (
		<>
			<Media lessThan="lg">
				<ContentSection hPadding="0px">
					<Carousel header={false} language={language}>
						{content.slides.map((slide, index) => (
							<VideoSlide
								key={index}
								person={slide.person}
								personLabel={slide.personLabel}
								videoId={content ? content.videoId : ""}
								quote={slide.quote}
								thumbnailUrl={content ? content.thumbnailUrl : undefined}
							/>
						))}
					</Carousel>
				</ContentSection>
			</Media>
			<Media greaterThanOrEqual="lg">
				<ContentSection vPadding="80px">
					<Carousel title={content.title} language={language}>
						{content.slides.map((slide: Slide, index: number) => (
							<VideoSlide
								key={index}
								person={slide.person}
								personLabel={slide.personLabel}
								videoId={content ? content.videoId : ""}
								quote={slide.quote}
								thumbnailUrl={content ? content.thumbnailUrl : undefined}
							/>
						))}
					</Carousel>
				</ContentSection>
			</Media>
		</>
	);
};

export default WhatOurCustomers;
